import { useState } from 'react';
import { Users, Check, IdCard, Phone, Mail, FileText } from 'lucide-react';

import { cn } from '@/lib/utils';
import type {
  AssistantClientSelection,
  AssistantClientSelectionCandidate,
} from '@/lib/api-client';

interface ClientSelectionCardProps {
  selection: AssistantClientSelection;
  onSelect: (candidate: AssistantClientSelectionCandidate) => void;
}

const CLIENT_TYPE_LABEL: Record<string, string> = {
  pf: 'Pessoa física',
  pj: 'Pessoa jurídica',
};

/**
 * Card de desambiguacao de cliente (select_client), espelhando a estetica do
 * ActionPreviewCard mas para ESCOLHA em vez de escrita: um candidato clicavel
 * por linha, sem botoes de confirmar/cancelar. Ao clicar, dispara `onSelect`
 * (o caller reinjeta a escolha no chat) e trava os demais candidatos — evita
 * clique duplo e evita reabrir a escolha depois de decidida.
 */
export function ClientSelectionCard({ selection, onSelect }: ClientSelectionCardProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  function handleClick(candidate: AssistantClientSelectionCandidate) {
    if (selectedId) return;
    setSelectedId(candidate.id);
    onSelect(candidate);
  }

  return (
    <div className="rounded-lg border border-[color-mix(in_oklch,var(--accent)_30%,var(--border))] bg-[var(--bg)] overflow-hidden">
      {/* Cabecalho */}
      <div className="flex items-center gap-1.5 border-b border-[var(--border)] px-3 py-2 text-xs font-semibold text-[var(--fg)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)]">
        <Users className="h-3.5 w-3.5 text-[var(--accent)]" />
        Mais de um cliente encontrado · escolha um
      </div>

      {/* Candidatos */}
      <div className="flex flex-col divide-y divide-[var(--border)]">
        {selection.candidates.map((candidate) => {
          const isSelected = selectedId === candidate.id;
          const isLocked = selectedId !== null;

          return (
            <button
              key={candidate.id}
              type="button"
              onClick={() => handleClick(candidate)}
              disabled={isLocked}
              className={cn(
                'flex flex-col items-start gap-1 px-3 py-2.5 text-left text-xs transition-colors',
                isSelected && 'bg-[color-mix(in_oklch,#16a34a_10%,transparent)]',
                !isLocked && 'hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] cursor-pointer',
                isLocked && !isSelected && 'opacity-50 cursor-not-allowed',
              )}
            >
              <div className="flex w-full flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />}
                <span className="font-semibold text-[var(--fg)] break-words">{candidate.name}</span>
                <span className="text-[var(--fg-muted)]">
                  · {CLIENT_TYPE_LABEL[candidate.type] ?? candidate.type}
                </span>
              </div>

              {candidate.document && (
                <span className="inline-flex items-center gap-1 text-[var(--fg-muted)] break-words">
                  <IdCard className="h-3 w-3 shrink-0" />
                  {candidate.document}
                </span>
              )}
              {candidate.phone && (
                <span className="inline-flex items-center gap-1 text-[var(--fg-muted)] break-words">
                  <Phone className="h-3 w-3 shrink-0" />
                  {candidate.phone}
                </span>
              )}
              {candidate.email && (
                <span className="inline-flex items-center gap-1 text-[var(--fg-muted)] break-words">
                  <Mail className="h-3 w-3 shrink-0" />
                  {candidate.email}
                </span>
              )}
              {candidate.cases.length > 0 && (
                <span className="inline-flex items-start gap-1 text-[var(--fg-muted)] break-words">
                  <FileText className="h-3 w-3 shrink-0 mt-px" />
                  {candidate.cases.join(', ')}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
